import React, { Suspense, useMemo } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { AppRoutes } from './router'
import { AuthProvider } from './auth/AuthProvider'
import { NotificationProvider } from './contexts/NotificationContext'
import { CopilotProvider } from './components/copilot/CopilotProvider'
import CopilotChat from './components/copilot/CopilotChat'
import { createQfinpricerTheme } from './theme/qfinpricerTheme'
import { AppearanceProvider, useAppearance } from './theme/AppearanceContext'
import { useAgentBroadcast } from './hooks/useAgentBroadcast'
import AgentModeIndicator from './components/agent/AgentModeIndicator'

// DemoPanel — lazy-loaded, only rendered when ?demo=true is present in the URL.
// Zero bundle impact in production; does NOT depend on react-router context.
const DemoPanel = React.lazy(() => import('./components/demo/DemoPanel'));
const AgentConfirmDialog = React.lazy(() => import('./components/agent/AgentConfirmDialog'))

function shouldShowDemo(): boolean {
  return new URLSearchParams(window.location.search).get('demo') === 'true';
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Application error:', error, info);
    // AuthRequiredError from workbench API calls — redirect to login immediately.
    if (error.name === 'AuthRequiredError') { window.location.replace('/login'); }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3, textAlign: 'center', bgcolor: 'background.default', color: 'text.primary' }}>
          <h1>Something went wrong</h1>
          <p>An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}
          >
            Refresh Page
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Inner component runs INSIDE DensityProvider so it can subscribe to the
// active density and regenerate the theme. Splitting it out keeps the rest
// of the providers from re-mounting on density change.
const AppInner: React.FC = () => {
  const {
    isAgentActive, currentAgentPage, highlightCount, stopAgent,
    isConfirmOpen, confirmRequest, closeConfirmDialog, wsSend,
  } = useAgentBroadcast()
  const { density, resolvedMode } = useAppearance()
  const theme = useMemo(() => createQfinpricerTheme(density, resolvedMode), [density, resolvedMode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <ErrorBoundary>
          <AuthProvider>
            <CopilotProvider>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                  </Box>
                }
              >
                <AppRoutes />
              </Suspense>
              <CopilotChat />
              <AgentModeIndicator
                isAgentActive={isAgentActive}
                currentAgentPage={currentAgentPage}
                highlightCount={highlightCount}
                stopAgent={stopAgent}
              />
              {isConfirmOpen && (
                <Suspense fallback={null}>
                  <AgentConfirmDialog
                    open={isConfirmOpen}
                    confirmRequest={confirmRequest}
                    onClose={closeConfirmDialog}
                    wsSend={wsSend}
                  />
                </Suspense>
              )}
              {shouldShowDemo() && (
                <Suspense fallback={null}>
                  <DemoPanel />
                </Suspense>
              )}
            </CopilotProvider>
          </AuthProvider>
        </ErrorBoundary>
      </NotificationProvider>
    </ThemeProvider>
  )
}

const App: React.FC = () => (
  <AppearanceProvider>
    <AppInner />
  </AppearanceProvider>
)

export default App;
